import React, { useState, useEffect } from 'react';
import { useCheckoutContext } from '../../context/CheckoutContext';
import { useCart } from '../../context/CartContext';
import {  View,  Text, StyleSheet,TouchableOpacity, Platform, ScrollView, useWindowDimensions} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores } from '../../styles/variablesCss';
import { BACKEND_URL } from '../../Config/Const';

// Función auxiliar para convertir string de fecha a objeto Date
const stringToDate = (fechaStr) => {
  if (!fechaStr || typeof fechaStr !== 'string') return null;
  
  // Formato dd/mm/yyyy
  const [dia, mes, anio] = fechaStr.split('/').map(num => parseInt(num));
  return new Date(anio, mes - 1, dia); // mes-1 porque en JS los meses van de 0-11
};

// Componente de calendario simplificado
const Calendario = ({ fechaSeleccionada, onSelectFecha, diasHabilitados = [] }) => {
  // Estado para controlar responsividad
  const windowDimensions = useWindowDimensions();
  const [windowWidth, setWindowWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : windowDimensions.width
  );
  const effectiveWidth = Platform.OS === 'web' ? windowWidth : windowDimensions.width;
  const isMobile = Platform.OS === 'web' ? effectiveWidth < 768 : true;
  const enableHorizontalScroll = isMobile || effectiveWidth < 1100;

  // Listener para cambios de tamaño de ventana
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Convertir fechaSeleccionada a objeto Date si es un string
  const fechaSeleccionadaObj = typeof fechaSeleccionada === 'string' ? 
    stringToDate(fechaSeleccionada) : fechaSeleccionada;
  // Si hay días habilitados de la API, usarlos; si no, generar días por defecto
  const diasDisponibles = diasHabilitados.length > 0 
    ? diasHabilitados // Ya son objetos Date procesados en fetchHorariosDisponibles
    : Array.from({ length: 7 }, (_, i) => {
        const fecha = new Date(); 
        fecha.setDate(fecha.getDate() + i);
        return fecha;
      });
      
  // Ordenar las fechas cronológicamente
  diasDisponibles.sort((a, b) => a - b);

  // Nombres cortos de los días de la semana en español
  const nombresDias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  
  // Nombres de los meses en español
  const nombresMeses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  // Verifica si una fecha es la seleccionada
  const esFechaSeleccionada = (fecha) => {
    return fechaSeleccionadaObj && 
           fecha.getDate() === fechaSeleccionadaObj.getDate() && 
           fecha.getMonth() === fechaSeleccionadaObj.getMonth() &&
           fecha.getFullYear() === fechaSeleccionadaObj.getFullYear();
  };

  // Estilos dinámicos para el calendario
  const dynamicStyles = {
    diaItem: {
      ...styles.diaItem,
      width: enableHorizontalScroll ? (isMobile ? 70 : 85) : '15%',
      padding: isMobile ? 8 : 12,
      marginRight: isMobile ? 8 : 0,
    },
    diaNombre: {
      ...styles.diaNombre,
      fontSize: isMobile ? 10 : 11,
    },
    diaNumero: {
      ...styles.diaNumero,
      fontSize: isMobile ? 16 : 20,
    },
  };

  const diasParaMostrar = isMobile ? diasDisponibles : diasDisponibles.slice(0, 4);

  return (
    <View style={styles.calendarioContainer}>
      <Text style={styles.calendarioTitulo}>Selecciona una fecha</Text>
      <ScrollView 
        horizontal={true}
        showsHorizontalScrollIndicator={enableHorizontalScroll}
        scrollEnabled={enableHorizontalScroll}
        contentContainerStyle={[
          styles.diasScrollContent,
          !enableHorizontalScroll && { gap: 8, marginBottom: 8, flexGrow: 1 }
        ]}
        style={styles.diasWrapper}
      >
        {diasParaMostrar.map((fecha, index) => (
          <TouchableOpacity 
            key={index}
            style={[
              dynamicStyles.diaItem,
              esFechaSeleccionada(fecha) && styles.diaSeleccionado
            ]}
            onPress={() => onSelectFecha(fecha)}
          >
            <Text style={[
              dynamicStyles.diaNombre, 
              esFechaSeleccionada(fecha) && styles.diaTextoSeleccionado
            ]}>
              {nombresDias[fecha.getDay()]}
            </Text>
            <Text style={[
              dynamicStyles.diaNumero, 
              esFechaSeleccionada(fecha) && styles.diaTextoSeleccionado
            ]}>
              {fecha.getDate()}
            </Text>
            <Text style={[
              styles.diaMes, 
              esFechaSeleccionada(fecha) && styles.diaTextoSeleccionado
            ]}>
              {nombresMeses[fecha.getMonth()]}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );
};

// Componente selector de horarios
const SelectorHorario = ({ horariosHabilitados = [], fechaSeleccionada }) => {
  const { fechaRetiroSeleccionada, codigoHorarioSeleccionado, setCodigoHorarioSeleccionado, setHorarioTexto,
    tipoEntrega, precioFleteDireccion } = useCheckoutContext();
  const { totales, calcularTotal } = useCart();
  
  // Calcular el total de productos (sin flete) para verificar si aplica flete gratis
  const subtotal = totales.subtotal || calcularTotal();
  const descuentoTotal = totales.descuentos || 0;
  const totalProductos = subtotal - descuentoTotal;
  
  // Calcular el flete final
  let fleteAMostrar = 0;
  
  // Si el total de productos es mayor a $100,000, el flete es gratis
  if (totalProductos > 100000) {
    fleteAMostrar = 0;
  } else {
    // Si no, calcular el flete normalmente
    const fleteCarrito = totales.precioFlete || 0;
    const fleteDireccion = precioFleteDireccion || 0;
    fleteAMostrar = parseInt(tipoEntrega) === 2 
      ? Math.max(fleteCarrito, fleteDireccion) 
      : fleteCarrito;
  }
  
  // Convertir fechaSeleccionada a objeto Date si es un string
  const fechaObj = typeof fechaSeleccionada === 'string' ? 
    stringToDate(fechaSeleccionada) : fechaSeleccionada;
  // Rangos de horarios disponibles - usar los de la API si están disponibles para la fecha seleccionada
  // Si no hay fecha seleccionada o no hay horarios disponibles, devolver array vacío
  const horariosDisponibles = (horariosHabilitados.length > 0 && (fechaSeleccionada || fechaRetiroSeleccionada))
    ? horariosHabilitados
      .filter(h => {
        // Si tenemos la fecha en formato dd/mm/yyyy
        if (typeof fechaSeleccionada === 'string') {
          // Convertir fechaSeleccionada de dd/mm/yyyy a componentes
          const [dia, mes, anio] = fechaSeleccionada.split('/').map(num => parseInt(num));
          
          // Convertir fecha del API a componentes
          const [apiAnio, apiMes, apiDia] = h.fecha.split('-').map(num => parseInt(num));
          
          // Comparar componentes (mes-1 porque en fechaRetiroSeleccionada los meses van de 1-12)
          return dia === apiDia && mes === apiMes && anio === apiAnio;
        }
        
        // Si tenemos el objeto Date (fechaObj)
        if (fechaObj) {
          // Convertir fecha del API a objeto Date para comparación
          const [year, month, day] = h.fecha.split('-').map(num => parseInt(num));
          const fechaHorario = new Date(year, month - 1, day);
          
          // Comparar por año, mes y día
          return fechaObj.getFullYear() === fechaHorario.getFullYear() &&
                 fechaObj.getMonth() === fechaHorario.getMonth() &&
                 fechaObj.getDate() === fechaHorario.getDate();
        }
        
        return false;
      })
      .map(h => ({
        id: `${h.codGrupo}`,
        texto: h.textoRango,
        dispEntrega: h.dispEntrega,
        dispRetiro: h.dispRetiro
      }))
    : [];
      
  // Ordenar los horarios por codGrupo (si hay alguno)
  if (horariosDisponibles.length > 0) {
    horariosDisponibles.sort((a, b) => Number(a.id) - Number(b.id));
  }

  // Renderizado diferente para web y móvil
  if (Platform.OS === 'web') {
    return (
      <View style={styles.selectorHorarioContainer}>
      {/*  <Text style={styles.selectorHorarioTitulo}>
          {fechaSeleccionada ? 'Selecciona un horario' : 'Primero selecciona una fecha'}
        </Text> */}
        
        {fechaSeleccionada && (
          horariosDisponibles.length > 0 ? (
            <View style={styles.selectContainer}>
              <select 
                style={styles.selectWeb}
                value={codigoHorarioSeleccionado || ''}
                onChange={(e) => {
                  // Usar el contexto directamente
                  const value = e.target.value;
                  setCodigoHorarioSeleccionado(value);
                  const sel = horariosDisponibles.find(h => String(h.id) === String(value));
                  setHorarioTexto(sel?.texto || '');
                }}
              >
                <option value="">Seleccionar horario</option>
                {horariosDisponibles.map((horario) => (
                  <option key={horario.id} value={horario.id}>
                    {horario.texto}
                  </option>
                ))}
              </select>
              <MaterialCommunityIcons 
                name="chevron-down" 
                size={20} 
                color={colores.textoPrincipal}
                style={styles.selectIcon}
              />
            </View>
          ) : (
            <Text style={styles.noHorariosText}>No hay horarios disponibles para la fecha seleccionada</Text>
          )
        )}

        {/* Aviso informativo - solo mostrar cuando el flete es gratis */}
        {parseInt(tipoEntrega) === 2 && fleteAMostrar === 0 && (
          <Text style={styles.avisoEnvio}>
            Asegurate de estar presente en el rango horario seleccionado. Contás con un sólo envío grátis.
          </Text>
        )}
      </View>
    );
  }

  // Versión para móvil usando botones
  return (
    <View style={styles.selectorHorarioContainer}>
      <Text style={styles.selectorHorarioTitulo}>
        {fechaRetiroSeleccionada ? 'Selecciona un horario' : 'Primero selecciona una fecha'}
      </Text>
      
      {fechaRetiroSeleccionada && (
        horariosDisponibles.length > 0 ? (
          <View style={styles.horariosGrid}>
            {horariosDisponibles.map((horario) => (
              <TouchableOpacity 
                key={horario.id} 
                style={[
                  styles.horarioItem,
                  codigoHorarioSeleccionado === horario.id && styles.horarioSeleccionado,
                  horario.dispRetiro <= 0 && styles.horarioSinDisponibilidad
                ]}
                onPress={() => {
                  if (horario.dispRetiro > 0) {
                    setCodigoHorarioSeleccionado(horario.id);
                    setHorarioTexto(horario.texto);
                  }
                }}
                disabled={horario.dispRetiro <= 0}
              >
                <Text style={[
                  styles.horarioTexto,
                  codigoHorarioSeleccionado === horario.id && styles.horarioTextoSeleccionado,
                  horario.dispRetiro <= 0 && styles.horarioTextoSinDisponibilidad
                ]}>
                  {horario.texto}
                </Text>
                <Text style={[
                  styles.disponibilidadTexto,
                  horario.dispRetiro <= 0 && styles.sinDisponibilidadTexto
                ]}>
                  {horario.dispRetiro > 0 ? `${horario.dispRetiro} disponibles` : 'Sin disponibilidad'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        ) : (
          <Text style={styles.noHorariosText}>No hay horarios disponibles para la fecha seleccionada</Text>
        )
      )}      

      {/* Aviso informativo - solo mostrar cuando el flete es gratis */}
      {parseInt(tipoEntrega) === 2 && fleteAMostrar === 0 && (
        <Text style={styles.avisoEnvio}>
          Asegurate de estar presente en el rango horario seleccionado. Contás con un sólo envío grátis.
        </Text>
      )}
    </View>
  ); 
};

const HorarioRetiro = () => {
  const { fechaRetiroSeleccionada, setFechaRetiroSeleccionada, codigoHorarioSeleccionado, setCodigoHorarioSeleccionado, setHorarioTexto, tipoEntrega } = useCheckoutContext();
  // Ya no necesitamos estado local porque usamos directamente el contexto
  const [diasHabilitados, setDiasHabilitados] = useState([]);
  const [horariosHabilitados, setHorariosHabilitados] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estado para controlar responsividad
  const windowDimensions = useWindowDimensions();
  const [windowWidth, setWindowWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : windowDimensions.width
  );
  const effectiveWidth = Platform.OS === 'web' ? windowWidth : windowDimensions.width;
  const isMobile = Platform.OS === 'web' ? effectiveWidth < 768 : true;

  // Listener para cambios de tamaño de ventana
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Función para formatear la fecha en formato dd/mm/yyyy
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    
    const dia = fecha.getDate().toString().padStart(2, '0');
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0'); // +1 porque los meses en JS van de 0 a 11
    const anio = fecha.getFullYear();
    
    return `${dia}/${mes}/${anio}`;
  };
  
  
  // Función para cargar los horarios disponibles desde la API
  const fetchHorariosDisponibles = async () => {
    setIsLoading(true);
    setError(null);
    try {

      let sucursalId = '';
      let cpEntrega = '';
      // saca la sucursal desde el local storage para la api en formate WEB, no funciona en celular
      if (Platform.OS === 'web') {
        sucursalId = localStorage.getItem('sucursalId');
      }

      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        try {
          const cpFromStorage = window.localStorage?.getItem('codigoPostal');
          if (cpFromStorage) {
            cpEntrega = cpFromStorage;
          }
        } catch (storageError) {
          // Silently handle storage errors
        }
      }
      
      // URL de la API para obtener horarios habilitados
      const params = new URLSearchParams();
      if (sucursalId) params.append('suc', sucursalId);
      // Usar entrega = 2 para Envío, entrega = 1 para Retiro
      const entregaParam = parseInt(tipoEntrega) === 2 ? '2' : '1';
      params.append('entrega', entregaParam);
      if (cpEntrega) params.append('codPostalEntrega', cpEntrega);
      const url = `${BACKEND_URL}/rest/fin/horarios?${params.toString()}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('No se pudieron cargar los horarios disponibles');
      }
      
      const data = await response.json();
      
      // Verificar si existe la tabla dsResultado en la respuesta
      if (data && data.dsResultado && data.dsResultado.horarios) {
        // Extraer los horarios del JSON
        const horarios = data.dsResultado.horarios;
        
        // Procesar las fechas únicas para el calendario (eliminar duplicados)
        const fechasUnicas = [...new Set(horarios.map(h => h.fecha))]
          .map(fechaStr => {
            // Crear la fecha con la fecha correcta ajustada a la zona horaria local
            const [year, month, day] = fechaStr.split('-').map(num => parseInt(num));
            // Creamos la fecha con los componentes (los meses en JS son 0-indexed)
            return new Date(year, month - 1, day);
          });
        
        // Guardar los datos procesados
        setDiasHabilitados(fechasUnicas);
        setHorariosHabilitados(horarios);
      } else {
        // Si no hay datos en la estructura esperada, usar valores por defecto
        setDiasHabilitados([]);
        setHorariosHabilitados([]);
      }
    } catch (err) {
      setError('No se pudieron cargar los horarios. Por favor, intenta nuevamente.');
      // Usar datos por defecto en caso de error
      setDiasHabilitados([]);
      setHorariosHabilitados([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Ya no necesitamos este efecto porque manejamos la conversión en el componente Calendario

  // Efecto para llamar a la función fetchHorariosDisponibles al montar el componente o cambiar la sucursal
  useEffect(() => {
    // Llamar a la función para obtener los horarios
    fetchHorariosDisponibles();
  }, []); // Se ejecuta cuando cambia la sucursal seleccionada

  useEffect(() => {
    if (isLoading || error) return;
    if (!Array.isArray(diasHabilitados) || diasHabilitados.length === 0) return;
    if (!Array.isArray(horariosHabilitados) || horariosHabilitados.length === 0) return;

    const diasOrdenados = [...diasHabilitados].sort((a, b) => a - b);
    const primeraFecha = diasOrdenados[0];
    if (!primeraFecha) return;

    const fechaStr = formatearFecha(primeraFecha);

    // Solo setear fecha por defecto si no hay una seleccionada
    // NO preseleccionar horario - el usuario debe seleccionarlo manualmente
    if (!fechaRetiroSeleccionada) {
      setFechaRetiroSeleccionada(fechaStr);
    }
  }, [
    diasHabilitados,
    horariosHabilitados,
    fechaRetiroSeleccionada,
    isLoading,
    error,
    setFechaRetiroSeleccionada,
  ]);


  // Estilos dinámicos para responsive
  const dynamicContainerStyles = {
    selectorContainer: {
      ...styles.selectorContainer,
      flexDirection: isMobile ? 'column' : 'row',
    },
    calendarioWrapper: {
      ...styles.calendarioWrapper,
      width: isMobile ? '100%' : '48%',
      flexGrow: 1,
      //minWidth: Platform.OS === 'web' ? 420 : undefined,
      marginBottom: isMobile ? 20 : 0,
    },
    horarioWrapper: {
      ...styles.horarioWrapper,
      width: isMobile ? '100%' : '48%',
      flexGrow: 1,
      //minWidth: Platform.OS === 'web' ? 420 : undefined,
      ...(Platform.OS === 'web'
        ? {
            display: 'flex',
            justifyContent: 'center',
          }
        : {}),
    },
  };

  return (
    <View style={styles.container}>
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Cargando horarios disponibles...</Text>
        </View>
      ) : error ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : (
        <>
          {/* Contenedor flex para mostrar calendario y selector de horario lado a lado */}
          <View style={dynamicContainerStyles.selectorContainer}>
            {/* Calendario para seleccionar fecha */}
            <View style={dynamicContainerStyles.calendarioWrapper}>
              <Calendario 
                fechaSeleccionada={fechaRetiroSeleccionada}
                onSelectFecha={(fecha) => {
                  setFechaRetiroSeleccionada(formatearFecha(fecha));
                }}
                diasHabilitados={diasHabilitados}
              />
            </View>

            {/* Selector de horario */}
            <View style={dynamicContainerStyles.horarioWrapper}>
              <SelectorHorario 
                fechaSeleccionada={fechaRetiroSeleccionada}
                horariosHabilitados={horariosHabilitados}
              />
            </View>
          </View>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 15,
    backgroundColor: colores.blanco,
    minHeight: 180, // Altura mínima para mantener consistencia visual
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  noHorariosText: {
    color: colores.grisOscuro,
    textAlign: 'center',
    fontSize: 16,
    marginTop: 15,
    marginBottom: 15,
    fontStyle: 'italic',
  },
  disponibilidadTexto: {
    fontSize: 12,
    color: colores.exito,
    marginTop: 4,
  },
  sinDisponibilidadTexto: {
    color: colores.error,
  },
  horarioSinDisponibilidad: {
    backgroundColor: colores.fondoClaro,
    opacity: 0.7,
    borderColor: colores.grisClaro,
  },
  horarioTextoSinDisponibilidad: {
    color: colores.grisOscuro,
  },
  loadingContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    height: 200,
  },
  loadingText: {
    fontSize: 16,
    color: colores.textoPrincipal,
    marginBottom: 10,
    textAlign: 'center',
  },
  errorContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    height: 200,
  },
  errorText: {
    fontSize: 16,
    color: colores.rojo,
    marginBottom: 15,
    textAlign: 'center',
  },
  calendarioContainer: {
    marginBottom: Platform.OS === 'web' ? 0 : 15,
  },
  calendarioTitulo: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 15,
  },
  diasWrapper: {
    flexDirection: 'row',
    width: '100%',
  },
  diasScrollContent: {
    justifyContent: 'flex-start',
  },
  diasContainer: {
    flexDirection: 'row',
    marginBottom: 15,
  },
  diaItem: {
    width: '24%',
    height: 78,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#EAEAEA',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    padding: 8,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.1,
          shadowRadius: 2,
          elevation: 1,
        }
    ),
  },
  diaSeleccionado: {
    backgroundColor: colores.rojo,
    borderColor: colores.rojo,
  },
  diaNombre: {
    fontSize: 14,
    fontWeight: '500',
    color: colores.textoPrincipal,
    textTransform: 'capitalize',
    marginBottom: 4,
  },
  diaNumero: {
    fontSize: 22,
    fontWeight: 'bold',
    color: colores.textoPrincipal,
    marginVertical: 4,
  },
  diaMes: {
    fontSize: 14,
    color: colores.textoPrincipal,
    marginTop: 2,
  },
  diaTextoSeleccionado: {
    color: colores.blanco,
  },
  selectorHorarioContainer: {
    marginBottom: Platform.OS === 'web' ? 0 : 15,
  },
  selectorHorarioTitulo: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 15,
  },
  avisoEnvio: {
    marginTop: 8,
    color: colores.grisOscuro,
    fontSize: 12,
    fontStyle: 'italic',
  },
  selectContainer: {
    position: 'relative',
    marginBottom: 20,
    width: '90%',
    alignSelf: 'center',
  },
  selectWeb: {
    width: '100%',
    height: 50,
    borderWidth: 1,
    borderColor: '#EAEAEA',
    borderRadius: 8,
    paddingHorizontal: 15,
    paddingLeft: 10,
    fontSize: 16,
    backgroundColor: colores.blanco,
    appearance: 'none',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
    fontWeight: '500',
  },
  selectIcon: {
    position: 'absolute',
    right: 15,
    top: 15,
    pointerEvents: 'none',
  },
  horariosGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 10,
  },
  horarioItem: {
    width: '48%',
    padding: 12,
    borderRadius: 8,
    backgroundColor: colores.fondoClaro,
    marginBottom: 10,
    marginRight: '2%',
    alignItems: 'center',
  },
  horarioSeleccionado: {
    backgroundColor: colores.rojo,
  },
  horarioTexto: {
    fontSize: 14,
    color: colores.textoPrincipal,
  },
  horarioTextoSeleccionado: {
    color: colores.blanco,
    fontWeight: 'bold',
  },
  selectorContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
   // marginBottom: 20,
    width: '100%',
    flexWrap: Platform.OS === 'web' ? 'wrap' : 'nowrap',
  },
  calendarioWrapper: {
    width: '48%',
  },
  horarioWrapper: {
    width: '48%',
  },
  botonConfirmar: {
    backgroundColor: colores.rojo,
    borderRadius: 8,
    paddingVertical: 15,
    alignItems: 'center',
    marginTop: 10,
    width: '100%',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 3,
          elevation: 3,
        }
    ),
  },
  botonDeshabilitado: {
    backgroundColor: colores.grisClaro,
    ...(Platform.OS === 'web' ? {} : { shadowOpacity: 0, elevation: 0 }),
  },
  botonConfirmarTexto: {
    color: colores.blanco,
    fontSize: 16,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  }
});

export default HorarioRetiro;
